import React, { useState, useEffect, useLayoutEffect } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';

const VEHICLE_TYPES = [
  { id: 'car', label: 'Car' },
  { id: 'suv', label: 'SUV' },
  { id: 'truck', label: 'Truck' },
];

const REAR_VLTS = [5, 15, 35];
const FRONT_VLTS = [5, 15, 35];
const WINDSHIELD_VLTS = [5, 15, 35];

function vltPad(vlt) {
  return String(vlt).padStart(2, '0');
}

function OverlayDiv({ vehicle, zone, vlt }) {
  if (!vlt) return null;
  return (
    <div
      data-testid={`overlay-${zone}`}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundImage: `url(/images/tint/${vehicle}-${zone}-${vltPad(vlt)}.png)`,
        backgroundSize: 'contain',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        pointerEvents: 'none',
      }}
    />
  );
}

function VltRow({ options, selected, onSelect, label, testIdPrefix }) {
  return (
    <div className="flex items-center gap-2 mb-2 md:mb-3">
      <span className="text-xs md:text-xs font-bold text-amber-500 uppercase tracking-wider w-16 md:w-20 shrink-0">{label}</span>
      <div className="flex gap-1.5 md:gap-1.5">
        {options.map((vlt) => (
          <button
            key={vlt}
            onClick={() => onSelect(selected === vlt ? 0 : vlt)}
            data-testid={`${testIdPrefix}-${vlt}`}
            className={`w-10 h-10 md:w-10 md:h-10 rounded-lg text-xs font-bold transition-all duration-200 border-2 ${
              selected === vlt
                ? 'bg-amber-500 border-amber-500 text-black shadow-lg shadow-amber-500/30'
                : 'bg-zinc-800 border-zinc-700 text-zinc-300 hover:border-amber-400 hover:text-white'
            }`}
          >
            {vltPad(vlt)}%
          </button>
        ))}
      </div>
    </div>
  );
}

const SimulatorPage = () => {
  const [vehicleType, setVehicleType] = useState('car');
  const [rearVlt, setRearVlt] = useState(0);
  const [frontVlt, setFrontVlt] = useState(0);
  const [windshieldVlt, setWindshieldVlt] = useState(0);

  const hasBack = vehicleType !== 'truck';

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  const handleReset = () => {
    setRearVlt(0);
    setFrontVlt(0);
    setWindshieldVlt(0);
  };

  const handleVehicleChange = (id) => {
    setVehicleType(id);
    handleReset();
  };

  return (
    <div className="h-[100dvh] md:h-auto md:min-h-screen bg-black flex flex-col overflow-hidden md:overflow-auto">
      <Header />

      {/* Mobile layout — fills entire screen */}
      <div className="md:hidden flex-1 flex flex-col pt-12 px-2 pb-1">
        {/* Car image — grows to fill available space */}
        <div
          className="relative w-full flex-1 min-h-0"
          data-testid="car-display"
          style={{ backgroundColor: '#fff', borderRadius: '10px', overflow: 'hidden' }}
        >
          <div
            data-testid="car-base-image"
            style={{
              position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
              backgroundImage: `url(/images/tint/${vehicleType}-white.png)`,
              backgroundSize: 'contain', backgroundPosition: 'center', backgroundRepeat: 'no-repeat',
            }}
          />
          <OverlayDiv vehicle={vehicleType} zone="rears" vlt={rearVlt} />
          {hasBack && <OverlayDiv vehicle={vehicleType} zone="back" vlt={rearVlt} />}
          <OverlayDiv vehicle={vehicleType} zone="front" vlt={frontVlt} />
          <OverlayDiv vehicle={vehicleType} zone="windshield" vlt={windshieldVlt} />
        </div>

        {/* Controls — fixed size at bottom */}
        <div
          className="grid grid-cols-2 gap-2 w-full bg-zinc-900/80 rounded-xl p-2.5 border border-zinc-800 shrink-0 mt-1"
          data-testid="controls-panel"
        >
          <div>
            <h3 className="text-xs font-bold text-white uppercase tracking-wider mb-2 border-b border-zinc-700 pb-1">
              Vehicle Type
            </h3>
            <div className="space-y-1.5" data-testid="vehicle-type-selector">
              {VEHICLE_TYPES.map((v) => (
                <button
                  key={v.id}
                  onClick={() => handleVehicleChange(v.id)}
                  data-testid={`vehicle-${v.id}`}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    vehicleType === v.id
                      ? 'bg-amber-500 text-black'
                      : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700 hover:text-white'
                  }`}
                >
                  {v.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-xs font-bold text-white uppercase tracking-wider mb-2 border-b border-zinc-700 pb-1">
              Window Tint (VLT)
            </h3>
            <VltRow options={FRONT_VLTS} selected={frontVlt} onSelect={setFrontVlt} label="Fronts" testIdPrefix="vlt-front" />
            <VltRow options={REAR_VLTS} selected={rearVlt} onSelect={setRearVlt} label="Rears" testIdPrefix="vlt-rear" />
            <VltRow options={WINDSHIELD_VLTS} selected={windshieldVlt} onSelect={setWindshieldVlt} label="W/Shield" testIdPrefix="vlt-ws" />
          </div>

          <div className="col-span-2 flex flex-col items-center gap-1">
            <div className="flex gap-2">
              <button
                onClick={() => window.location.href = '/?openCategory=window-tint#pricing'}
                data-testid="im-done-btn"
                className="px-8 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-zinc-300 text-sm font-medium hover:bg-amber-500 hover:border-amber-500 hover:text-black transition-all duration-200"
              >
                I'm Done
              </button>
              <button
                onClick={handleReset}
                data-testid="reset-btn"
                className="px-8 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-zinc-300 text-sm font-medium hover:bg-amber-500 hover:border-amber-500 hover:text-black transition-all duration-200"
              >
                Reset Tint
              </button>
            </div>
            <p className="text-zinc-600 text-[10px]">Please Check Your Local Tint Laws</p>
          </div>
        </div>
      </div>

      {/* Desktop layout */}
      <main className="hidden md:flex flex-1 flex-col pt-24 pb-16">
        <div className="bg-black flex-1">
          <div className="max-w-6xl mx-auto px-8 py-10">
            <div
              className="relative max-w-4xl mx-auto mb-8"
              data-testid="car-display-desktop"
              style={{ aspectRatio: '1200 / 572', backgroundColor: '#fff', borderRadius: '12px', overflow: 'hidden' }}
            >
              <div
                style={{
                  position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                  backgroundImage: `url(/images/tint/${vehicleType}-white.png)`,
                  backgroundSize: 'contain', backgroundPosition: 'center', backgroundRepeat: 'no-repeat',
                }}
              />
              <OverlayDiv vehicle={vehicleType} zone="rears" vlt={rearVlt} />
              {hasBack && <OverlayDiv vehicle={vehicleType} zone="back" vlt={rearVlt} />}
              <OverlayDiv vehicle={vehicleType} zone="front" vlt={frontVlt} />
              <OverlayDiv vehicle={vehicleType} zone="windshield" vlt={windshieldVlt} />
            </div>

            <div
              className="grid grid-cols-2 gap-6 max-w-3xl mx-auto bg-zinc-900/80 rounded-2xl p-6 border border-zinc-800"
              data-testid="controls-panel-desktop"
            >
              <div>
                <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-4 border-b border-zinc-700 pb-2">
                  Vehicle Type
                </h3>
                <div className="space-y-2">
                  {VEHICLE_TYPES.map((v) => (
                    <button
                      key={v.id}
                      onClick={() => handleVehicleChange(v.id)}
                      data-testid={`vehicle-${v.id}-desktop`}
                      className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                        vehicleType === v.id
                          ? 'bg-amber-500 text-black'
                          : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700 hover:text-white'
                      }`}
                    >
                      {v.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-4 border-b border-zinc-700 pb-2">
                  Window Tint (VLT)
                </h3>
                <VltRow options={REAR_VLTS} selected={rearVlt} onSelect={setRearVlt} label="Rears" testIdPrefix="vlt-rear" />
                <VltRow options={FRONT_VLTS} selected={frontVlt} onSelect={setFrontVlt} label="Fronts" testIdPrefix="vlt-front" />
                <VltRow options={WINDSHIELD_VLTS} selected={windshieldVlt} onSelect={setWindshieldVlt} label="Windshield" testIdPrefix="vlt-ws" />
              </div>

              <div className="col-span-2 flex flex-col items-center gap-2">
                <div className="flex gap-3">
                  <button
                    onClick={() => window.location.href = '/?openCategory=window-tint#pricing'}
                    data-testid="im-done-btn-desktop"
                    className="px-10 py-2.5 rounded-lg bg-zinc-800 border border-zinc-700 text-zinc-300 text-sm font-medium hover:bg-amber-500 hover:border-amber-500 hover:text-black transition-all duration-200"
                  >
                    I'm Done
                  </button>
                  <button
                    onClick={handleReset}
                    data-testid="reset-btn-desktop"
                    className="px-10 py-2.5 rounded-lg bg-zinc-800 border border-zinc-700 text-zinc-300 text-sm font-medium hover:bg-amber-500 hover:border-amber-500 hover:text-black transition-all duration-200"
                  >
                    Reset Tint
                  </button>
                </div>
                <p className="text-zinc-600 text-xs">Please Check Your Local Tint Laws</p>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </main>
    </div>
  );
};

export default SimulatorPage;
